/**
 * useIntakePermissions
 * ────────────────────
 * Feb 2026 — Phase 2 of B2 IntakeWizard extraction.
 *
 * Thin React hook around `createIntakePermissions`.  Pulls `user` from
 * AuthContext and `roles` from StaticDataContext, then memoizes the four
 * helpers.  `intakeFormConfig` is owned by the caller (typically
 * IntakeWizardProvider, which fetches and caches it) and passed in.
 */
import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStaticData } from '../context/StaticDataContext';
import { createIntakePermissions } from '../utils/intakePermissions';

export function useIntakePermissions(intakeFormConfig) {
  const { user } = useAuth();
  const { roles } = useStaticData();

  const userRoleName = useMemo(() => {
    if (user?.role_id && roles?.length) {
      const role = roles.find((r) => r.id === user.role_id);
      if (role?.name) return role.name.toLowerCase();
    }
    return (user?.role || '').toLowerCase();
  }, [user?.role_id, user?.role, roles]);

  return useMemo(
    () => createIntakePermissions({ user, roles, userRoleName, intakeFormConfig }),
    [user, roles, userRoleName, intakeFormConfig],
  );
}

export default useIntakePermissions;
